import React, { useCallback, useEffect, useRef, useState } from "react";
import Icons from "../icons";
import Loader from "./Loader";
import { GherkinLines, isGherkin, scenarioCount, NO_SCENARIO } from "./TestCaseRow";
import { Column, EmptyState } from "./RequirementBoardView";
import AppPageCreateArtifact, { visionWsUrl } from "./AppPageCreateArtifact";
import AppPageReview from "./AppPageReview";
import ConfirmModal from "./ConfirmModal";
import AppPageVariables from "./AppPageVariables";
import AppPageActivityButton from "./AppPageActivityLog";
import AppPageDeleteButton from "./AppPageDelete";
import ListSearch, { NoMatches, matchesQuery } from "./ListSearch";
import { ChecksBadge, ChecksList } from "./AppPageChecks";
import { useToast } from "./Toast";
import { runSocketNote } from "./RunSocketNotice";
import { openRunSocket } from "../hooks/useRunSocket";
import { appPageRunPoll } from "../hooks/runPolls";
import { plural } from "./TestRunsPanel";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

const cardClass = (active) =>
  [
    "flex w-full flex-col gap-1 rounded-xl border p-3 text-left transition",
    active
      ? "border-blue-400 bg-blue-50 ring-1 ring-blue-400/40"
      : "border-slate-200 bg-card hover:border-blue-200 hover:bg-blue-50/40",
  ].join(" ");

function pageLine(p, generating) {
  if (generating || p.running?.kind === "tests") return "Generating tests…";
  if (p.running?.kind === "artifacts") return "Creating Artifacts…";
  if (!p.tests) return "Not generated";
  return `${p.tests} tests · ${p.automated} automated`;
}

/* App Page: a board like the Requirement Board, with the App Page in place of
   Epic and Story. Its Tests (one per generated Cucumber scenario, Jira-like
   keys such as RecordDowntime-1) are stored in Cortex. Create Artifact and
   Review open in place of the board. */
export default function AppPageView({ userkey }) {
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [pagesError, setPagesError] = useState("");
  const [pageQuery, setPageQuery] = useState("");
  const [selectedPage, setSelectedPage] = useState(null);
  const [data, setData] = useState(null); // {tests, running, bdd, generated_at}
  const [testsLoading, setTestsLoading] = useState(false);
  const [testsError, setTestsError] = useState("");
  const [openTests, setOpenTests] = useState(() => new Set());
  const [screen, setScreen] = useState("board"); // board | create | review
  const [createKeys, setCreateKeys] = useState(null);
  /* What was just done (tests or Artifacts created or recreated, a delete, values saved), as a toast. */
  const [showToast, toast] = useToast(5000);
  /* The App Page whose tests "Recreate" is waiting to confirm. */
  const [recreatePage, setRecreatePage] = useState(null);
  /* The Cucumber generation in progress (one at a time). */
  const [gen, setGen] = useState({ page: null, running: false, stages: {}, error: "" });
  const wsRef = useRef(null);
  const finishedRef = useRef(false);
  const [connection, setConnection] = useState("idle");

  const loadPages = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}app-pages`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setPages(Array.isArray(body.data) ? body.data : []);
      setPagesError("");
    } catch (e) {
      setPagesError(`Could not load App Pages: ${e.message}`);
    } finally {
      setPagesLoading(false);
    }
  }, []);

  const loadTests = useCallback(async (page) => {
    if (!page) return null;
    setTestsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}app-pages/${encodeURIComponent(page)}/tests`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setData(body.data);
      setTestsError("");
      return body.data;
    } catch (e) {
      setTestsError(`Could not load tests: ${e.message}`);
      return null;
    } finally {
      setTestsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const stopStreams = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        /* already closed */
      }
      wsRef.current = null;
    }
  }, []);
  useEffect(() => stopStreams, [stopStreams]);

  const finish = useCallback(
    (page) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      stopStreams();
      setGen((g) => ({ ...g, running: false }));
      loadTests(page);
      loadPages();
    },
    [loadPages, loadTests, stopStreams]
  );

  /* Follow a Cucumber generation run: live over the Vision socket, falling
     back to polling the run row if the socket drops. */
  const follow = useCallback(
    (page, runId) => {
      stopStreams();
      finishedRef.current = false;
      const onEvent = (ev) => {
        if (ev.type === "hello") {
          const stages = {};
          (ev.snapshot?.stages || []).forEach((s) => {
            stages[s.name] = { status: s.status, message: s.message };
          });
          setGen((g) => ({ ...g, stages }));
        } else if (ev.type === "stage") {
          setGen((g) => ({ ...g, stages: { ...g.stages, [ev.stage]: { status: ev.status, message: ev.message } } }));
        } else if (ev.type === "error") {
          setGen((g) => ({ ...g, error: ev.message || "The run failed." }));
        } else if (ev.type === "tests_saved") {
          const fresh = !ev.updated && !ev.retired && !ev.deleted && !ev.unchanged;
          showToast(`${page} tests have been ${fresh ? "created" : "recreated"} successfully.`);
        } else if (ev.type === "done") {
          finish(page);
        }
      };
      /* A drop reconnects, then polls the run row until it has ended. */
      wsRef.current = openRunSocket(visionWsUrl(runId), {
        onEvent,
        onStatus: setConnection,
        poll: appPageRunPoll(runId, (run) => {
          if (run && run.status !== "running" && run.error) setGen((g) => ({ ...g, error: run.error }));
        }),
        pollMs: 4000,
        onEnd: () => {
          setConnection("idle");
          finish(page);
        },
      });
    },
    [finish, showToast, stopStreams]
  );

  const selectPage = async (page) => {
    if (page === selectedPage) return;
    setSelectedPage(page);
    setData(null);
    setOpenTests(new Set());
    const loaded = await loadTests(page);
    /* A generation already running for this page (started earlier, or by a
       teammate): show its progress. */
    const running = loaded?.running;
    if (running?.kind === "tests" && !gen.running) {
      setGen({ page, running: true, stages: {}, error: "" });
      follow(page, running.run_id);
    }
  };

  const generateTests = async (pageArg) => {
    const page = typeof pageArg === "string" ? pageArg : selectedPage;
    if (!page) return;
    if (page !== selectedPage) selectPage(page);
    setGen({ page, running: true, stages: {}, error: "" });
    try {
      const res = await fetch(`${API_BASE_URL}app-pages/${encodeURIComponent(page)}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userkey: userkey || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.data?.run_id) throw new Error(body?.detail || `HTTP ${res.status}`);
      follow(page, body.data.run_id);
      loadPages();
    } catch (e) {
      setGen((g) => ({ ...g, running: false, error: `Could not start generation: ${e.message}` }));
    }
  };

  const toggleTest = (key) =>
    setOpenTests((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const tests = data?.tests || [];
  const activeTests = tests.filter((t) => t.status === "active");
  const shownPages = pages.filter((p) => matchesQuery(pageQuery, p.app_page_id));
  const genHere = gen.page === selectedPage;
  const generatingHere = genHere && gen.running;
  const artifactsRunning = data?.running?.kind === "artifacts";
  const busy = generatingHere || artifactsRunning;

  /* Create Artifacts (header): the default selection — every test not yet
     automated. A test's own Create Artifact: just that test. */
  const notAutomated = activeTests.filter((t) => !t.automated).length;
  /* Every test already has an Artifact: the header's button creates them again. */
  const allAutomated = activeTests.length > 0 && notAutomated === 0;
  const openCreate = (keys) => {
    setCreateKeys(keys);
    setScreen("create");
  };

  const backToBoard = () => {
    setScreen("board");
    setCreateKeys(null);
    loadTests(selectedPage);
    loadPages();
  };

  if (screen === "create" && selectedPage) {
    return (
      <AppPageCreateArtifact
        appPage={selectedPage}
        tests={tests}
        userkey={userkey}
        initialKeys={createKeys}
        onCancel={backToBoard}
        onDone={(saved) => {
          backToBoard();
          const created = saved?.saved?.length || 0;
          const recreated = saved?.replaced?.length || 0;
          const what = [created && `${plural(created, "Artifact")} created`, recreated && `${plural(recreated, "Artifact")} recreated`]
            .filter(Boolean)
            .join(" and ");
          if (what) showToast(`${what} successfully.`);
        }}
      />
    );
  }
  if (screen === "review" && selectedPage) {
    return (
      <AppPageReview
        appPage={selectedPage}
        tests={tests}
        onBack={backToBoard}
        onCreateArtifact={(keys) => {
          setCreateKeys(keys);
          setScreen("create");
        }}
      />
    );
  }

  let testsBody;
  if (!selectedPage) {
    testsBody = <EmptyState icon={Icons.JiraXrayTest} text="Select an App Page to see its tests." />;
  } else if (generatingHere) {
    testsBody = (
      <Loader.Overlay
        variant="guide"
        label="Creating tests…"
        sublabel={runSocketNote(connection) || `Writing the test scenarios of ${selectedPage}`}
        delay={0}
      />
    );
  } else if (testsLoading && !data) {
    testsBody = (
      <Loader.Overlay variant="guide" label="Loading tests…" sublabel={`Fetching the tests of ${selectedPage}`} delay={0} />
    );
  } else if (testsError) {
    testsBody = <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{testsError}</p>;
  } else if (!tests.length) {
    testsBody = (
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400">
        No tests yet. Create them from this App Page's configuration.
      </p>
    );
  } else {
    testsBody = (
      <ul className="space-y-2">
        {tests.map((x) => {
          const open = openTests.has(x.test_key);
          const steps = x.steps || [];
          return (
            <li
              key={x.test_key}
              className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm transition hover:border-blue-200"
            >
              <div className="flex w-full flex-wrap items-center gap-2 px-3 py-3 transition hover:bg-blue-50/40">
                <button
                  type="button"
                  onClick={() => toggleTest(x.test_key)}
                  aria-expanded={open}
                  className="flex min-w-0 flex-1 basis-64 items-center gap-2 text-left"
                >
                  <Icons.ChevronRight
                    className={["h-3.5 w-3.5 flex-none text-slate-400 transition-transform", open ? "rotate-90" : ""].join(" ")}
                  />
                  <span className="flex-none whitespace-nowrap text-sm font-medium text-blue-600">{x.test_key}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800" title={x.title || ""}>
                    {x.title || "(no title)"}
                  </span>
                </button>
                {scenarioCount(steps) > 0 && (
                  <span className="text-[0.6875rem] text-slate-400">
                    {scenarioCount(steps)} step{scenarioCount(steps) === 1 ? "" : "s"}
                  </span>
                )}
                {x.status === "retired" && (
                  <span
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500"
                    title="Not in the latest generation; kept because it has an Artifact"
                  >
                    Retired
                  </span>
                )}
                {x.drifted && (
                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">steps changed</span>
                )}
                <ChecksBadge report={x.verification} />
                {x.automated ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                    <Icons.Check className="h-3.5 w-3.5" /> Automated
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">Not automated</span>
                )}
                {/* Who created and re-created this test, its Artifact and its executions, and when. */}
                <AppPageActivityButton compact title={`${x.test_key} — Test`} scope={{ test_key: x.test_key }} />
                {/* Deletes the Test with its Artifact and executions (logged only when the dialog's box is ticked). */}
                <AppPageDeleteButton
                  what="test"
                  testKey={x.test_key}
                  name={x.title}
                  userkey={userkey}
                  disabled={busy}
                  disabledReason="Wait until the run in progress ends"
                  onDeleted={() => {
                    showToast(`${x.test_key} has been deleted successfully.`);
                    loadTests(selectedPage);
                    loadPages();
                  }}
                />
                {x.status === "active" && (
                  <button
                    type="button"
                    onClick={() => openCreate([x.test_key])}
                    disabled={busy}
                    title={
                      x.automated
                        ? `Create the Artifact of ${x.test_key} again; the one it has is replaced and kept as a version`
                        : `Create an Artifact for ${x.test_key} only`
                    }
                    className="inline-flex flex-none items-center gap-1 rounded-lg border border-blue-200 bg-white px-2.5 py-1 text-[0.75rem] font-semibold text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {x.automated ? (
                      <>
                        <Icons.Plus className="h-3.5 w-3.5" /> Recreate Artifact
                      </>
                    ) : (
                      <>
                        <Icons.Plus className="h-3.5 w-3.5" /> Create Artifact
                      </>
                    )}
                  </button>
                )}
              </div>
              {open && (
                <div className="border-t border-slate-100 bg-slate-50/60">
                  {isGherkin(steps) ? (
                    <div className="px-3 py-2">
                      <GherkinLines steps={steps} compact />
                    </div>
                  ) : (
                    <p className="px-3 py-2.5 text-xs text-slate-400">{NO_SCENARIO}</p>
                  )}
                  {x.verification && (
                    <div className="px-3 pb-3">
                      <ChecksList report={x.verification} title="Checks against the page" />
                    </div>
                  )}
                  {Object.keys(x.variables || {}).length > 0 && (
                    <div className="px-3 pb-3">
                      <AppPageVariables
                        testKey={x.test_key}
                        variables={x.variables}
                        userkey={userkey}
                        onSaved={(_t, saved) => {
                          showToast(`${x.test_key}: ${saved}`);
                          loadTests(selectedPage);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col">
      {toast}
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">App Pages</h1>
        <p className="mt-1 text-sm text-slate-500">Tests generated from PlantOPS app pages.</p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        {/* Column 1 — App Pages */}
        <div className="flex min-h-0 flex-none flex-col lg:w-72 xl:w-80 3xl:w-96 4xl:w-[30rem]">
          <Column
            icon={Icons.Grid}
            title="App Pages"
            accent="blue"
            count={shownPages.length}
            subHeader={
              <div className="flex-none border-b border-slate-200 px-2.5 py-2">
                <ListSearch value={pageQuery} onChange={setPageQuery} placeholder="Search App Pages…" label="Search App Pages" />
              </div>
            }
          >
            {pagesLoading ? (
              <Loader.Overlay variant="guide" label="Loading App Pages…" sublabel="Fetching the App Pages and their tests" delay={0} />
            ) : pagesError ? (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{pagesError}</p>
            ) : shownPages.length === 0 && pageQuery.trim() ? (
              <NoMatches query={pageQuery} />
            ) : (
              shownPages.map((p) => {
                const generatingThis = gen.running && gen.page === p.app_page_id;
                return (
                  <div
                    key={p.app_page_id}
                    role="button"
                    tabIndex={0}
                    aria-label={p.app_page_id}
                    onClick={() => selectPage(p.app_page_id)}
                    onKeyDown={(e) =>
                      (e.key === "Enter" || e.key === " ") && e.target === e.currentTarget && selectPage(p.app_page_id)
                    }
                    className={`${cardClass(p.app_page_id === selectedPage)} cursor-pointer`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="min-w-0 flex-1">
                        {/* A long name is cut with "…" before the button; the full name shows on hover. */}
                        <span className="block truncate text-sm font-medium leading-snug text-slate-800" title={p.app_page_id}>
                          {p.app_page_id}
                        </span>
                        <span className="block truncate text-[0.6875rem] text-slate-400">{pageLine(p, generatingThis)}</span>
                      </span>
                      {/* Create (or Recreate, after a confirmation) the page's Cucumber tests. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (p.tests) setRecreatePage(p);
                          else generateTests(p.app_page_id);
                        }}
                        disabled={gen.running || Boolean(p.running)}
                        title={p.tests ? "Create the scenarios again; test keys are kept" : "Create the Cucumber tests"}
                        className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[0.75rem] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
                      >
                        {generatingThis ? <Loader.Inline size={12} /> : <Icons.Plus className="h-3.5 w-3.5" />}
                        {generatingThis ? "Creating…" : p.tests ? "Recreate" : "Create"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </Column>
        </div>

        {/* Column 2 — Tests, like the Requirement Board's Tests panel */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60 shadow-sm">
          {/* Side by side (lg), at least as tall as the App Pages column's header (2.5rem of padding and content
              + its 1px line), so the two dividers meet. It wraps when the column is too narrow for its buttons. */}
          <header className="flex flex-none flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-100/70 px-3 py-1.5 lg:min-h-[2.8125rem] lg:py-1">
            <Icons.JiraXrayTest />
            <h3 className="flex-none whitespace-nowrap text-sm font-semibold text-slate-800">Tests</h3>
            <span className="flex-none rounded-full bg-white px-2 py-0.5 text-[0.6875rem] font-semibold text-slate-500 ring-1 ring-slate-200">
              {selectedPage ? activeTests.length : 0}
            </span>
            {/* The page's whole Feature, as the checks against its test model found it. */}
            {selectedPage && !generatingHere && (
              <ChecksBadge report={data?.bdd?.verification} what="Feature" label="Feature" />
            )}
            <span className="flex-1" aria-hidden="true" />
            {/* Who created and re-created this App Page's tests and Artifacts, and ran them. */}
            {selectedPage && (
              <AppPageActivityButton
                title={`${selectedPage} — Tests`}
                scope={{ app_page_id: selectedPage }}
                className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[0.8125rem] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
              />
            )}
            <button
              type="button"
              onClick={() => openCreate(allAutomated ? activeTests.map((t) => t.test_key) : null)}
              disabled={!activeTests.length || busy}
              title={
                activeTests.length === 0
                  ? "Generate tests first — Artifact scripts are built from them"
                  : allAutomated
                    ? "Every test has an Artifact: create them again (each replaced, the old one kept as a version)"
                    : "Create Artifact scripts from this App Page's tests"
              }
              className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-blue-600 bg-blue-600 px-3 py-1.5 text-[0.8125rem] font-semibold text-white shadow-sm transition hover:border-blue-700 hover:bg-blue-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
            >
              <Icons.Plus className="h-4 w-4" />{" "}
              {allAutomated
                ? activeTests.length === 1
                  ? "Recreate Artifact"
                  : "Recreate Artifacts"
                : notAutomated === 1
                  ? "Create Artifact"
                  : "Create Artifacts"}
            </button>
            <button
              type="button"
              onClick={() => setScreen("review")}
              disabled={!tests.length}
              title="Open each test with its Artifact"
              className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[0.8125rem] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
            >
              Review <Icons.ChevronRight className="h-4 w-4" />
            </button>
          </header>

          {genHere && !gen.running && gen.error && (
            <div className="flex-none space-y-2 border-b border-slate-200 px-3 py-2.5">
              {gen.error && (
                <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-700">
                  <Icons.Alert className="mt-px h-3.5 w-3.5 flex-none" />
                  <span className="min-w-0 flex-1 break-words">{gen.error}</span>
                  <button type="button" onClick={generateTests} className="flex-none font-semibold underline">
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}
          {artifactsRunning && (
            <p className="flex-none border-b border-slate-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Create Artifact is running for this App Page. Generating is disabled until it finishes.
            </p>
          )}

          <div className="cq-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">{testsBody}</div>
        </div>
      </div>

      {recreatePage && (
        <ConfirmModal
          tone="primary"
          icon={Icons.Spark}
          title="Recreate the tests?"
          message={`${recreatePage.app_page_id} has ${recreatePage.tests} ${
            recreatePage.tests === 1 ? "test" : "tests"
          }. Recreating will sync tests with the current configuration, updating changed tests and retiring or removing obsolete ones. Do you want to continue?`}
          confirmLabel="Recreate"
          busyLabel="Starting…"
          onCancel={() => setRecreatePage(null)}
          onConfirm={() => {
            const page = recreatePage.app_page_id;
            setRecreatePage(null);
            generateTests(page);
          }}
        />
      )}
    </div>
  );
}
